import { routeCatalog } from './catalog';
import type {
	NavigationIntent,
	QueryParams,
	QueryPrimitive,
	ResolvedRoute,
	RouteDefinition,
	RouteParams
} from './types';
import { OrganizationType } from './types';

const WILDCARD_SEGMENT = '*';

const isWildcardPattern = (pattern: string): boolean =>
	pattern === WILDCARD_SEGMENT || pattern.endsWith(`/${WILDCARD_SEGMENT}`);

type RouteMatcher = {
	pattern: string;
	route: RouteDefinition;
};

const buildQueryString = (query?: QueryParams) => {
	if (!query) {
		return '';
	}

	const searchParams = new URLSearchParams();

	Object.entries(query).forEach(([key, value]) => {
		if (Array.isArray(value)) {
			value.forEach((entry) => {
				searchParams.append(key, String(entry));
			});
			return;
		}

		if (value !== undefined && value !== null) {
			searchParams.append(key, String(value));
		}
	});

	const serialized = searchParams.toString();

	return serialized ? `?${serialized}` : '';
};

export class RouteRegistry {
	readonly #routesByIntent: ReadonlyMap<NavigationIntent, RouteDefinition>;
	readonly #routes: readonly RouteDefinition[];
	readonly #matchers: readonly RouteMatcher[];

	constructor(routes: readonly RouteDefinition[] = routeCatalog) {
		this.#routes = routes;
		this.#routesByIntent = new Map(
			routes.map((route) => [route.intent, route])
		);

		const matchers = routes.flatMap((route) =>
			(route.matchPaths ?? []).map((pattern) => ({ pattern, route }))
		);

		this.#matchers = [
			...matchers.filter(({ pattern }) => !isWildcardPattern(pattern)),
			...matchers.filter(({ pattern }) => isWildcardPattern(pattern))
		];
	}

	getRoute(intent: NavigationIntent): RouteDefinition | null {
		return this.#routesByIntent.get(intent) ?? null;
	}

	resolve(args: {
		intent: NavigationIntent;
		params?: RouteParams;
		query?: QueryParams;
	}): ResolvedRoute {
		const route = this.getRoute(args.intent);

		if (!route) {
			throw new Error(`Unknown navigation intent: ${args.intent}`);
		}

		if (!route.buildPath) {
			throw new Error(`Cannot navigate to match-only route: ${args.intent}`);
		}

		const params = args.params ?? {};
		const query = args.query ?? {};
		const path = route.buildPath(params);
		const url = `${path}${buildQueryString(query)}`;

		return {
			appId: route.appId,
			intent: route.intent,
			params,
			path,
			query,
			requiresAuth: route.requiresAuth ?? false,
			url
		};
	}

	override(
		intent: NavigationIntent,
		patch: Partial<
			Pick<RouteDefinition, 'buildPath' | 'matchPaths' | 'requiresAuth'>
		>
	): RouteRegistry {
		if (!this.getRoute(intent)) {
			throw new Error(`Unknown navigation intent: ${intent}`);
		}

		const patched = this.#routes.map((route) => {
			if (route.intent !== intent || route.buildPath === undefined) {
				return route;
			}

			return { ...route, ...patch };
		});

		return new RouteRegistry(patched);
	}

	match(url: string): ResolvedRoute | null {
		const parsedUrl = new URL(url, 'http://routing-client.local');
		const path = parsedUrl.pathname;
		const query = this.#parseQuery(parsedUrl.searchParams);

		for (const { pattern, route } of this.#matchers) {
			const params = this.#matchPath(pattern, path);

			if (params) {
				return {
					appId: route.appId,
					intent: route.intent,
					params,
					path,
					query,
					requiresAuth: route.requiresAuth ?? false,
					url: `${path}${parsedUrl.search}`
				};
			}
		}

		return null;
	}

	#matchPath(pattern: string, path: string): RouteParams | null {
		const patternParts = this.#splitPath(pattern);
		const pathParts = this.#splitPath(path);
		const hasWildcard = isWildcardPattern(pattern);
		const fixedParts = hasWildcard ? patternParts.slice(0, -1) : patternParts;

		if (
			hasWildcard
				? pathParts.length < fixedParts.length
				: pathParts.length !== fixedParts.length
		) {
			return null;
		}

		const params: RouteParams = {};

		for (let index = 0; index < fixedParts.length; index += 1) {
			const patternPart = fixedParts[index];
			const pathPart = pathParts[index];

			if (patternPart?.startsWith(':')) {
				this.#assignParam(params, patternPart.slice(1), pathPart ?? '');
				continue;
			}

			if (patternPart !== pathPart) {
				return null;
			}
		}

		if (pathParts[0] === OrganizationType.Customer) {
			params.organizationType = OrganizationType.Customer;
		}

		if (pathParts[0] === OrganizationType.Partner) {
			params.organizationType = OrganizationType.Partner;
		}

		return params;
	}

	#assignParam(params: RouteParams, key: string, value: string): void {
		params[key] = decodeURIComponent(value);
	}

	#parseQuery(searchParams: URLSearchParams): QueryParams {
		const query: QueryParams = {};

		searchParams.forEach((value, key) => {
			const existing = query[key];

			if (Array.isArray(existing)) {
				query[key] = [...existing, value];
				return;
			}

			if (this.#isQueryPrimitive(existing)) {
				query[key] = [existing, value];
				return;
			}

			query[key] = value;
		});

		return query;
	}

	#splitPath(path: string): string[] {
		return path.split('/').filter(Boolean);
	}

	#isQueryPrimitive(value: unknown): value is QueryPrimitive {
		return (
			typeof value === 'string' ||
			typeof value === 'number' ||
			typeof value === 'boolean'
		);
	}
}
